import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Role } from '../../../common/models/role.model';
import { RoleService } from '../../../services/role.service';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';

@Component({
  selector: 'app-roles',
  standalone: false,
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent implements OnInit, OnDestroy {
  @ViewChild('roleModal') roleModalRef!: TemplateRef<any>;

  protected roles: Role[] = [];
  protected isDataLoading = false;
  protected isSaving = false;
  protected isEditMode = false;
  protected modalRef: NgbModalRef | null = null;
  protected roleForm!: FormGroup;
  protected formError: any = null;

  private editingRoleId: string | null = null;

  constructor(
    private roleService: RoleService,
    private modalService: NgbModal,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadRoles();
  }

  private initForm(): void {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      // Lowercase letters, digits and underscores, starting with a letter - matches the
      // API's own validator so a bad code is caught before the request goes out.
      code: ['', [Validators.required, Validators.maxLength(50), Validators.pattern('^[a-z][a-z0-9_]*$')]],
      description: [''],
    });
  }

  private loadRoles(): void {
    this.isDataLoading = true;
    this.roleService
      .list()
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: (roles: Role[]) => {
          this.roles = roles;
        },
      });
  }

  protected onAddRole(): void {
    this.isEditMode = false;
    this.editingRoleId = null;
    this.formError = null;
    this.roleForm.reset({ name: '', code: '', description: '' });
    this.roleForm.get('code')?.enable();

    const options: NgbModalOptions = { backdrop: 'static' };
    this.modalRef = this.modalService.open(this.roleModalRef, options);
  }

  protected onEditRole(role: Role): void {
    this.isEditMode = true;
    this.editingRoleId = role.id;
    this.formError = null;

    this.roleForm.patchValue({ name: role.name, code: role.code, description: role.description });
    // Users reference roles by code, so it cannot be changed after creation.
    this.roleForm.get('code')?.disable();

    const options: NgbModalOptions = { backdrop: 'static' };
    this.modalRef = this.modalService.open(this.roleModalRef, options);
  }

  protected onRetireRole(role: Role): void {
    const ref = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    ref.componentInstance.title = 'Retire Role';
    ref.componentInstance.message = `Are you sure you want to retire "${role.name}"?`;
    ref.componentInstance.warning =
      'It will no longer be offered when assigning a role. Users who already hold it keep it.';

    ref.result.then(() => {
      this.roleService.setActive(role.id, false).subscribe({
        next: (updated: Role) => this.replaceRole(updated),
      });
    });
  }

  protected onRestoreRole(role: Role): void {
    this.roleService.setActive(role.id, true).subscribe({
      next: (updated: Role) => this.replaceRole(updated),
    });
  }

  protected onSubmit(): void {
    if (this.roleForm.invalid) return;

    this.isSaving = true;
    this.formError = null;

    const values = this.roleForm.getRawValue();

    if (this.isEditMode && this.editingRoleId) {
      // `code` is intentionally left out: the API rejects any change to it.
      const payload = {
        name: values.name.trim(),
        description: values.description?.trim() || '',
      };

      this.roleService
        .update(this.editingRoleId, payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (updated: Role) => {
            this.replaceRole(updated);
            this.modalRef?.dismiss();
          },
          error: error => {
            this.formError = error;
          },
        });
    } else {
      const payload = {
        name: values.name.trim(),
        code: values.code.trim().toLowerCase(),
        description: values.description?.trim() || '',
      };

      this.roleService
        .create(payload)
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe({
          next: (created: Role) => {
            this.roles = [...this.roles, created].sort((a, b) => a.name.localeCompare(b.name));
            this.modalRef?.dismiss();
          },
          error: error => {
            this.formError = error;
          },
        });
    }
  }

  protected isInvalid(field: string): boolean {
    const ctrl = this.roleForm.get(field);
    return (ctrl?.invalid && (ctrl.touched || ctrl.dirty)) ?? false;
  }

  /**
   * The code the entered name should map to, e.g. "Sales Officer" -> "sales_officer".
   * Shown as a hint while adding a role.
   */
  protected get suggestedCode(): string {
    const name: string = this.roleForm?.get('name')?.value ?? '';
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  protected applySuggestedCode(): void {
    this.roleForm.get('code')?.setValue(this.suggestedCode);
    this.roleForm.get('code')?.markAsDirty();
  }

  private replaceRole(updated: Role): void {
    const idx = this.roles.findIndex(r => r.id === updated.id);
    if (idx !== -1) this.roles[idx] = updated;
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
  }
}
